import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { CheckCircle2, Clock, UserCheck, Trash2, Link as LinkIcon, Activity, Calendar, Mic, GraduationCap, LogOut } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SecurityEventsPanel from './SecurityEventsPanel';
import MinuteRequestsPanel from './MinuteRequestsPanel';
import SiteFooter from '../components/SiteFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [adminSecret, setAdminSecret] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersSort, setUsersSort] = useState('engagement');
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchRequests = async (secret) => {
    const s = secret || adminSecret;
    if (!s) return;
    setLoading(true);
    try {
      // Use the enriched endpoint so each row can render a mini activity
      // cluster (logins · active days · coaching sessions) under the email.
      const response = await axios.get(`${API}/admin/beta-requests-with-activity`, {
        params: { admin_secret: s },
      });
      setRequests(response.data.requests || []);
      setAuthenticated(true);
    } catch (error) {
      toast.error('Invalid admin credentials');
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Users table — separate fetch so a slow admin/users query never blocks
  // the primary Beta Requests view. Re-fired whenever `usersSort` changes.
  const fetchUsers = async (secret, sort) => {
    const s = secret || adminSecret;
    if (!s) return;
    try {
      const response = await axios.get(`${API}/admin/users`, {
        params: { admin_secret: s, sort: sort || usersSort },
      });
      setUsers(response.data.users || []);
    } catch (error) {
      // Non-fatal — the primary beta-requests panel remains usable.
      const msg = error.response?.data?.detail || 'Failed to load users';
      toast.error(msg);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await axios.post(`${API}/beta/approve`, {
        request_id: requestId,
        admin_secret: adminSecret
      });
      // The approval generates an invite code but we do NOT send an email
      // automatically — admin copies the invite link and shares it manually.
      toast.success('Request approved. Copy the invite link to share.');
      fetchRequests();
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to approve request';
      toast.error(message);
    }
  };

  /**
   * Build the shareable magic link an admin can paste into an email or DM.
   * The RegisterPage reads ?email= and ?code= from the URL and prefills
   * both fields, so the recipient only needs to pick a password.
   */
  const buildInviteLink = (request) => {
    if (!request.invite_code) return null;
    const url = new URL('/register', window.location.origin);
    url.searchParams.set('email', request.email);
    url.searchParams.set('code', request.invite_code);
    return url.toString();
  };

  const handleCopyInviteLink = async (request) => {
    const link = buildInviteLink(request);
    if (!link) {
      toast.error('No invite code available for this request');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`Invite link for ${request.email} copied to clipboard`);
    } catch (err) {
      // Fallback for older browsers or non-secure contexts (rare with HTTPS).
      // Show the link inline so the admin can copy manually.
      window.prompt('Copy the invite link:', link);
    }
  };

  const handleDelete = async (requestId, email) => {
    if (!window.confirm(`Are you sure you want to delete the request for ${email}? This cannot be undone.`)) {
      return;
    }
    
    setDeletingId(requestId);
    try {
      await axios.delete(`${API}/beta/requests/${requestId}`, {
        params: { admin_secret: adminSecret }
      });
      toast.success(`Request for ${email} deleted`);
      fetchRequests();
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete request';
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleAuth = (e) => {
    e.preventDefault();
    fetchRequests();
    fetchUsers();
  };

  // When the user changes the Users-table sort, re-fetch (server does the
  // sort so the numbers stay canonical). Only runs after authentication.
  useEffect(() => {
    if (authenticated) {
      fetchUsers(undefined, usersSort);
    }
  }, [usersSort]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-zinc-400" />;
      case 'approved':
        return <CheckCircle2 className="w-4 h-4 text-zinc-400" />;
      case 'registered':
        return <UserCheck className="w-4 h-4 text-white" />;
      default:
        return null;
    }
  };

  // Admin "Log out" — the admin secret lives in component state (never
  // persisted to localStorage), so dropping it + flipping `authenticated`
  // back to false is enough to return to the credentials gate. We also
  // clear the loaded tables so a shoulder-surfer can't peek at the
  // last-fetched beta list.
  const handleAdminLogout = () => {
    setAdminSecret('');
    setAuthenticated(false);
    setRequests([]);
    setUsers([]);
    toast.success('Signed out of the Oracle.');
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
        <div className="overlay-noise" />
        <div className="fixed top-5 right-5 z-30">
          <ThemeToggle size="sm" className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm" />
        </div>
        <div className="relative z-10 w-full max-w-md">
          <h1 
            className="text-5xl md:text-6xl font-light tracking-tight text-zinc-100 mb-8 text-center"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            The Oracle
          </h1>
          
          <form onSubmit={handleAuth} className="space-y-6" data-testid="admin-auth-form">
            <Input
              type="password"
              placeholder="Enter admin secret"
              value={adminSecret}
              onChange={(e) => setAdminSecret(e.target.value)}
              className="bg-transparent border-b border-white/20 focus:border-white text-white placeholder:text-zinc-500 px-0 py-4 rounded-none focus:ring-0 transition-all duration-300 w-full font-light text-lg"
              data-testid="admin-secret-input"
            />
            
            <button
              type="submit"
              className="threshold-button w-full rounded-sm px-8 py-5 text-lg md:text-xl"
              data-testid="admin-login-button"
              disabled={loading}
              aria-label="Enter admin panel"
            >
              <span>{loading ? 'Authenticating…' : 'Enter'}</span>
              {!loading && (
                <span className="threshold-button-arrow" aria-hidden="true">→</span>
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <a 
              href="/" 
              className="text-xs text-zinc-500 hover:text-zinc-500 transition-colors"
            >
              ← Back to Landing
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <div className="overlay-noise" />
      <div className="fixed top-5 right-5 z-30 flex items-center gap-2">
        <ThemeToggle size="sm" className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm" />
        <Button
          onClick={handleAdminLogout}
          variant="ghost"
          size="sm"
          className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm text-zinc-300 hover:text-white hover:bg-stone-900/60 h-9 px-3 gap-2"
          data-testid="admin-logout-btn"
          title="Log out of the Oracle"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-xs font-mono uppercase tracking-widest">Log out</span>
        </Button>
      </div>
      
      <div className="relative z-10 p-6 md:p-12">
        {/* Header */}
        <div className="mb-12">
          <h1 
            className="text-4xl md:text-5xl font-light tracking-tight text-zinc-100 mb-2"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            The Oracle
          </h1>
          <p className="text-sm font-mono uppercase tracking-widest text-zinc-400">
            Beta Request Management
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2">Pending</p>
            <p className="text-3xl font-light text-white">
              {requests.filter(r => r.status === 'pending').length}
            </p>
          </div>
          <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2">Approved</p>
            <p className="text-3xl font-light text-white">
              {requests.filter(r => r.status === 'approved').length}
            </p>
          </div>
          <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2">Registered</p>
            <p className="text-3xl font-light text-white">
              {requests.filter(r => r.status === 'registered').length}
            </p>
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="requests-table">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Email</th>
                  <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Status</th>
                  <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Requested</th>
                  <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Code</th>
                  <th className="text-right p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr 
                    key={request.request_id} 
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`request-row-${request.request_id}`}
                  >
                    <td className="p-4 text-zinc-300 font-light">
                      <div>{request.email}</div>
                      {/* Inline activity cluster — surfaces how engaged an
                          approved/registered user is without leaving this
                          row. Rendered only when at least one signal is
                          non-zero to avoid pushing 0/0/0 for pending users. */}
                      {request.activity && (
                        request.activity.login_count > 0 ||
                        request.activity.coaching_session_count > 0
                      ) && (
                        <div
                          className="mt-1.5 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500"
                          data-testid={`activity-inline-${request.request_id}`}
                        >
                          <span className="flex items-center gap-1" title="Logins">
                            <Activity className="w-3 h-3" />
                            {request.activity.login_count}
                          </span>
                          <span className="flex items-center gap-1" title="Days active">
                            <Calendar className="w-3 h-3" />
                            {request.activity.active_days_count}d
                          </span>
                          <span className="flex items-center gap-1" title="Coaching sessions">
                            <Mic className="w-3 h-3" />
                            {request.activity.coaching_session_count}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(request.status)}
                        <span className="text-sm text-zinc-400 font-mono">{request.status}</span>
                      </div>
                    </td>
                    <td className="p-4 text-zinc-500 text-sm font-mono">
                      {new Date(request.requested_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-zinc-400 font-mono text-sm">
                      {request.invite_code || '-'}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {request.status === 'pending' && (
                          <Button
                            onClick={() => handleApprove(request.request_id)}
                            className="frost-button rounded-sm px-6 py-2 text-sm"
                            data-testid={`approve-button-${request.request_id}`}
                          >
                            Approve
                          </Button>
                        )}
                        {/* Copy invite link — only surfaced for requests that
                            actually have an invite code (approved or already
                            registered). "Registered" users don't need the
                            link but exposing it lets an admin re-send if the
                            first invitation was lost before signup. */}
                        {request.invite_code && request.status !== 'pending' && (
                          <Button
                            onClick={() => handleCopyInviteLink(request)}
                            className="bg-transparent border border-white/20 text-zinc-200 hover:bg-white/5 hover:border-white/40 rounded-sm px-3 py-2 text-sm font-medium transition-all flex items-center gap-2"
                            title="Copy the shareable invite link"
                            data-testid={`copy-link-button-${request.request_id}`}
                          >
                            <LinkIcon className="w-4 h-4" />
                            <span className="hidden md:inline">Copy invite link</span>
                          </Button>
                        )}
                        <Button
                          onClick={() => handleDelete(request.request_id, request.email)}
                          disabled={deletingId === request.request_id}
                          className="bg-transparent border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 rounded-sm px-3 py-2 text-sm font-medium transition-all"
                          data-testid={`delete-button-${request.request_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {requests.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-400">No beta requests yet</p>
          </div>
        )}

        {/* -------------------------------------------------------------
             Users & Activity table
             -------------------------------------------------------------
             Renders every registered SAGE user (not requests — actual
             account holders) with the three engagement counters plus
             derived signals (anchor + learn-topic counts, tutorial
             completed?). Default sort is by coaching_session_count desc
             so power users surface first; admin can flip to recent /
             alphabetical via the pill toggle. */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2
                className="text-2xl font-light text-zinc-100"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Users & Activity
              </h2>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">
                {users.length} registered · engagement metrics
              </p>
            </div>
            <div className="flex items-center gap-2" role="group" aria-label="Sort users by">
              {[
                { id: 'engagement', label: 'Engagement' },
                { id: 'recent', label: 'Recently seen' },
                { id: 'email', label: 'A–Z' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setUsersSort(opt.id)}
                  className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
                    usersSort === opt.id
                      ? 'border-white/40 text-white bg-white/5'
                      : 'border-white/10 text-zinc-500 hover:text-zinc-300 hover:border-white/20'
                  }`}
                  data-testid={`users-sort-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Email</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">
                      <span className="inline-flex items-center gap-1">
                        <Activity className="w-3.5 h-3.5" /> Logins
                      </span>
                    </th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" /> Active days
                      </span>
                    </th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">
                      <span className="inline-flex items-center gap-1">
                        <Mic className="w-3.5 h-3.5" /> Sessions
                      </span>
                    </th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">
                      <span className="inline-flex items-center gap-1">
                        <GraduationCap className="w-3.5 h-3.5" /> Topics
                      </span>
                    </th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Anchors</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Tour</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400" title="Terms, Privacy, and Beta Confidentiality acknowledged at the current version">Policies</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Last seen</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">First seen</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-zinc-400">Auth</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.user_id}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      data-testid={`user-row-${u.user_id}`}
                    >
                      <td className="p-4 text-zinc-300 font-light text-sm">
                        {u.email}
                      </td>
                      <td className="p-4 text-zinc-200 font-mono text-sm">
                        {u.login_count}
                      </td>
                      <td className="p-4 text-zinc-200 font-mono text-sm">
                        {u.active_days_count}
                      </td>
                      <td className="p-4 text-zinc-200 font-mono text-sm">
                        {u.coaching_session_count}
                      </td>
                      <td className="p-4 text-zinc-500 font-mono text-sm">
                        {u.learn_topic_count}
                      </td>
                      <td className="p-4 text-zinc-500 font-mono text-sm">
                        {u.anchor_count}
                      </td>
                      <td className="p-4 text-sm">
                        {u.tutorial_completed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" aria-label="Tour completed" />
                        ) : (
                          <span className="text-zinc-600 font-mono">—</span>
                        )}
                      </td>
                      <td
                        className="p-4 text-sm"
                        data-testid={`user-policy-ack-${u.user_id}`}
                        title={u.policy_version ? `Acknowledged version ${u.policy_version}` : 'Not yet acknowledged'}
                      >
                        {u.policy_acknowledged ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" aria-label="Policies acknowledged at current version" />
                        ) : (
                          <span className="text-zinc-600 font-mono">—</span>
                        )}
                      </td>
                      <td className="p-4 text-zinc-500 text-sm font-mono">
                        {u.last_seen_at
                          ? new Date(u.last_seen_at).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td className="p-4 text-zinc-500 text-sm font-mono">
                        {u.first_seen_at
                          ? new Date(u.first_seen_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="p-4 text-zinc-500 text-xs font-mono uppercase tracking-widest">
                        {u.auth_method}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {users.length === 0 && (
            <div className="text-center py-8">
              <p className="text-zinc-500 text-sm">
                No registered users yet — approvals turn into rows here after
                a user completes registration.
              </p>
            </div>
          )}
        </div>

        {/* Minute Requests — beta testers asking for extra Auto time.
            Admin can approve/reject here; both actions email the user. */}
        <MinuteRequestsPanel adminSecret={adminSecret} />

        {/* Security audit log — blocked uploads, prompt-injection detections,
            rate-limit hits. Independent fetch inside its own component so a
            slow query never blocks the primary Beta Requests / Users views. */}
        <SecurityEventsPanel adminSecret={adminSecret} />
      </div>
      <SiteFooter />
    </div>
  );
};

export default AdminPanel;